import React, { useState, useEffect, useCallback } from 'react';
import Typography from '../../../basic/Typography';
import { useNewNG } from '../context/NewNGContext';
import { newNGApi } from '../services/newNGApi';
import styles from '../NewNG.module.css';

interface Citation {
  number: string;
  url: string;
  domain: string;
}

interface AnnotatedCommentaryProps {
  className?: string;
}

export const AnnotatedCommentary: React.FC<AnnotatedCommentaryProps> = ({ className = '' }) => {
  const { state } = useNewNG();
  const [commentary, setCommentary] = useState<string>('');
  const [citations, setCitations] = useState<Citation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredCitation, setHoveredCitation] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hasValidPosition, setHasValidPosition] = useState(false);

  // Extract domain from URL for display
  const extractDomain = useCallback((url: string): string => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return url;
    }
  }, []);

  // Process annotated details to extract citations and create interactive text
  const processAnnotatedText = useCallback((annotatedText: string, sources: string[]): { processedText: string; citations: Citation[] } => {
    const citationMap: Citation[] = [];

    // Find all citation patterns like [1], [2], etc.
    const citationRegex = /\[(\d+)\]/g;
    let match;
    const foundNumbers = new Set<string>();

    while ((match = citationRegex.exec(annotatedText)) !== null) {
      const citationNumber = match[1];
      if (!foundNumbers.has(citationNumber)) {
        foundNumbers.add(citationNumber);
        const sourceIndex = parseInt(citationNumber) - 1;
        if (sourceIndex >= 0 && sourceIndex < sources.length) {
          citationMap.push({
            number: citationNumber,
            url: sources[sourceIndex],
            domain: extractDomain(sources[sourceIndex])
          });
        }
      }
    }

    // Replace citations with interactive spans
    const processedText = annotatedText.replace(
      /\[(\d+)\]/g,
      '<span class="newng-citation" data-citation="$1">[$1]</span>'
    );

    return { processedText, citations: citationMap };
  }, [extractDomain]);

  // Load commentary from current job's news events
  useEffect(() => {
    const loadCommentary = async () => {
      if (!state.currentJob?.job_id) {
        setCommentary('');
        setCitations([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Get enhanced job info with aggregation data
        const enhancedInfo = await newNGApi.getEnhancedJobInfo(state.currentJob.job_id);

        if (enhancedInfo?.aggregation?.news_events?.events && enhancedInfo.aggregation.news_events.events.length > 0) {
          // Combine all annotated details from news events
          const allCommentary = enhancedInfo.aggregation.news_events.events
            .map((event: any) => event.details_annotate || event.details_spoken || '')
            .filter(Boolean)
            .join('\n\n');

          // Collect all sources
          const allSources = enhancedInfo.aggregation.news_events.events
            .flatMap((event: any) => event.sources || []);

          if (allCommentary) {
            const { processedText, citations: extractedCitations } = processAnnotatedText(allCommentary, allSources);
            setCommentary(processedText);
            setCitations(extractedCitations);
          } else {
            setCommentary('No annotated commentary available for this news video.');
            setCitations([]);
          }
        } else {
          setCommentary('No commentary data available for this news video.');
          setCitations([]);
        }
      } catch (error) {
        console.error('Failed to load commentary:', error);
        setCommentary('Unable to load commentary data.');
        setCitations([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCommentary();
  }, [state.currentJob?.job_id, processAnnotatedText]);

  // Handle citation hover
  const handleCitationMouseEnter = (citationNumber: string) => {
    setHoveredCitation(citationNumber);
  };

  const handleCitationMouseLeave = () => {
    setHoveredCitation(null);
    setHasValidPosition(false);
  };



  // Handle mouse events on the commentary container
  const handleCommentaryMouseMove = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const citationElement = target.closest('.newng-citation');

    if (citationElement) {
      const citationNumber = citationElement.getAttribute('data-citation');
      if (citationNumber) {
        // Calculate position
        const commentaryContainer = e.currentTarget as HTMLElement;
        const containerRect = commentaryContainer.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        // Set position and mark as valid
        setTooltipPosition({ x: x + 10, y: y - 10 });
        setHasValidPosition(true);

        // Show tooltip
        if (citationNumber !== hoveredCitation) {
          handleCitationMouseEnter(citationNumber);
        }
      }
    } else if (hoveredCitation) {
      handleCitationMouseLeave();
    }
  };

  const handleCommentaryMouseLeave = () => {
    if (hoveredCitation) {
      handleCitationMouseLeave();
    }
  };





  if (isLoading) {
    return (
      <div className={`${styles['newng-commentary-panel']} ${className}`}>
        <Typography variant="news-subtitle" component="div" className={styles['newng-commentary-header']}>
          Annotated Commentary
        </Typography>
        <div className={styles['newng-commentary-loading']}>
          <Typography variant="body2">Loading commentary...</Typography>
        </div>
      </div>
    );
  }

  const hoveredCitationData = hoveredCitation ? citations.find(c => c.number === hoveredCitation) : null;

  return (
    <div className={`${styles['newng-commentary-panel']} ${className}`}>
      <Typography variant="news-subtitle" component="div" className={styles['newng-commentary-header']}>
        Annotated Commentary
      </Typography>

      <div
        className={styles['newng-commentary-content']}
        onMouseMove={handleCommentaryMouseMove}
        onMouseLeave={handleCommentaryMouseLeave}
      >
        <div
          className={styles['newng-commentary-text']}
          dangerouslySetInnerHTML={{ __html: commentary }}
        />

        {/* Citation tooltip */}
        {hoveredCitationData && hasValidPosition && (
          <div
            className={styles['newng-citation-tooltip']}
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`
            }}
            onMouseLeave={handleCitationMouseLeave}
          >
            <div className={styles['newng-citation-tooltip-content']}>
              <Typography variant="caption" component="div" className={styles['newng-citation-number']}>
                Source [{hoveredCitationData.number}]
              </Typography>
              <Typography variant="body2" component="div" className={styles['newng-citation-domain']}>
                {hoveredCitationData.domain}
              </Typography>
            </div>
          </div>
        )}
      </div>

      {/* Sources list */}
      {citations.length > 0 && (
        <div className={styles['newng-commentary-sources']}>
          <Typography variant="overline" component="div" className={styles['newng-sources-header']}>
            Sources:
          </Typography>
          <div className={styles['newng-sources-list']}>
            {citations.map((citation) => (
              <div key={citation.number} className={styles['newng-source-item']}>
                <Typography variant="caption" component="span" className={styles['newng-source-number']}>
                  [{citation.number}]
                </Typography>
                <a
                  href={citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles['newng-source-link']}
                >
                  <Typography variant="caption" component="span">
                    {citation.domain}
                  </Typography>
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};